// Hooks de dados de insights (início, campanhas, detalhe, estimativa) e mudanças em campanhas.
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CampaignActionResponse, CampaignDetailResponse, CampaignsResponse, DashboardResponse, EstimateResponse } from '@impulso/shared/api/insights'
import { api } from '../../lib/api'

export const insightsKeys = {
  all: (companyId: string) => ['insights', companyId] as const,
  dashboard: (companyId: string, days: number) => ['insights', companyId, 'dashboard', days] as const,
  campaigns: (companyId: string, days: number) => ['insights', companyId, 'campaigns', days] as const,
  campaign: (companyId: string, campaignId: string, days: number) => ['insights', companyId, 'campaign', campaignId, days] as const,
  estimate: (companyId: string, campaignId: string | null, dailyBudget: number, days: number) => ['insights', companyId, 'estimate', campaignId, dailyBudget, days] as const,
}

/** Enquanto alguma conta ainda sincroniza, recarrega a cada 20 s. */
const syncingRefetch = (q: { state: { data?: { syncing?: boolean } } }) => (q.state.data?.syncing ? 20_000 : false)

export function useDashboard(companyId: string, days = 30) {
  return useQuery({
    queryKey: insightsKeys.dashboard(companyId, days),
    queryFn: () => api.get<DashboardResponse>(`/companies/${companyId}/dashboard?days=${days}`),
    refetchInterval: syncingRefetch,
  })
}

export function useCampaigns(companyId: string, days = 30) {
  return useQuery({
    queryKey: insightsKeys.campaigns(companyId, days),
    queryFn: () => api.get<CampaignsResponse>(`/companies/${companyId}/campaigns?days=${days}`),
    refetchInterval: syncingRefetch,
  })
}

export function useCampaignDetail(companyId: string, campaignId: string, days = 30) {
  return useQuery({
    queryKey: insightsKeys.campaign(companyId, campaignId, days),
    queryFn: () => api.get<CampaignDetailResponse>(`/companies/${companyId}/campaigns/${campaignId}?days=${days}`),
  })
}

export function useEstimate(companyId: string, dailyBudget: number, opts: { campaignId?: string | null; days?: number; enabled?: boolean } = {}) {
  const days = opts.days ?? 30
  const campaignId = opts.campaignId ?? null
  return useQuery({
    queryKey: insightsKeys.estimate(companyId, campaignId, dailyBudget, days),
    queryFn: () => api.get<EstimateResponse>(`/companies/${companyId}/estimate?dailyBudget=${dailyBudget}&days=${days}${campaignId ? `&campaignId=${campaignId}` : ''}`),
    enabled: (opts.enabled ?? true) && dailyBudget > 0,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  })
}

export function useSetCampaignStatus(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { campaignId: string; status: 'active' | 'paused' }) =>
      api.post<CampaignActionResponse>(`/companies/${companyId}/campaigns/${v.campaignId}/status`, { status: v.status }),
    onSettled: () => qc.invalidateQueries({ queryKey: insightsKeys.all(companyId) }),
  })
}

export function useSetDailyBudget(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { campaignId: string; dailyBudget: number }) =>
      api.post<CampaignActionResponse>(`/companies/${companyId}/campaigns/${v.campaignId}/budget`, { dailyBudget: v.dailyBudget }),
    onSettled: () => qc.invalidateQueries({ queryKey: insightsKeys.all(companyId) }),
  })
}
